import { ActionReducer, INIT, UPDATE } from '@ngrx/store';
import { AppState } from '../appState/app.state';
import { PersistedAppContext } from '../appContext/app-context.model';
import AES from 'crypto-js/aes';
import Utf8 from 'crypto-js/enc-utf8';
import { environment } from 'src/environments/environment.prod';

const SECRET_KEY = environment.enjmkey;
const APP_CONTEXT_STATE_KEY = 'appContextState';

function encrypt(data: any): string {
  return AES.encrypt(JSON.stringify(data), SECRET_KEY).toString();
}

function decrypt(ciphertext: string): any {
  const bytes = AES.decrypt(ciphertext, SECRET_KEY);
  const decrypted = bytes.toString(Utf8);
  return JSON.parse(decrypted);
}

function parseJson<T>(value: string | null): T | null {
  if (!value) {
    return null;
  }
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

function normalizePersistedAppContext(
  value: unknown
): PersistedAppContext | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const context = value as Partial<PersistedAppContext> & {
    persistedAppContext?: Partial<PersistedAppContext>;
  };

  const nested = context.persistedAppContext;
  const source = nested ?? context;

  if (
    !source.selectedCountry &&
    !source.originalUser &&
    !source.originalRole &&
    !source.isSuperAdmin
  ) {
    return null;
  }

  return {
    selectedCountry: source.selectedCountry ?? null,
    originalUser: source.originalUser ?? '',
    originalRole: source.originalRole ?? '',
    isSuperAdmin: source.isSuperAdmin ?? 'N',
  };
}

function getPersistedAppContextFromStorage(): PersistedAppContext | null {
  return normalizePersistedAppContext(
    parseJson<unknown>(localStorage.getItem(APP_CONTEXT_STATE_KEY))
  );
}

function persistAppContextForRefresh(
  appContext: AppState['appContext'] | undefined
): void {
  const persisted = appContext?.persistedAppContext;

  const hasPersistedData =
    persisted &&
    (persisted.selectedCountry ||
      persisted.originalUser ||
      persisted.originalRole ||
      persisted.isSuperAdmin !== 'N');

  if (hasPersistedData) {
    localStorage.setItem(APP_CONTEXT_STATE_KEY, JSON.stringify(persisted));
  } else {
    localStorage.removeItem(APP_CONTEXT_STATE_KEY);
  }
}

function clearPersistedAppContext(): void {
  localStorage.removeItem(APP_CONTEXT_STATE_KEY);
}

export function localStorageMetaReducer(
  reducer: ActionReducer<AppState>
): ActionReducer<AppState> {
  // In-memory only — used so the beforeunload handler always has the
  // latest state without appContext ever touching localStorage in between.
  let latestState: AppState | undefined;

  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      persistAppContextForRefresh(latestState?.appContext);
    });
  }

  return (state, action) => {
    if (action.type === '[User] Logout') {
      localStorage.removeItem('userState');
      clearPersistedAppContext();
      const resetState = reducer(undefined, { type: INIT });
      latestState = resetState;
      return resetState;
    }

    if (action.type === INIT || action.type === UPDATE) {
      const encryptedUser = localStorage.getItem('userState');
      const user = encryptedUser ? decrypt(encryptedUser) : null;
      const persistedAppContext = getPersistedAppContextFromStorage();

      const newState = reducer(state, action);

      // Hydration done — drop the temporary bridge entry immediately so
      // appContext doesn't linger in localStorage during runtime.
      clearPersistedAppContext();

      const hydratedState = {
        ...newState,
        user: user ?? newState.user,
        appContext: {
          ...newState.appContext,
          persistedAppContext:
            persistedAppContext ?? newState.appContext.persistedAppContext,
        },
      };

      latestState = hydratedState;
      return hydratedState;
    }

    const nextState = reducer(state, action);

    if (nextState?.user) {
      localStorage.setItem('userState', encrypt(nextState.user));
    } else {
      localStorage.removeItem('userState');
    }

    latestState = nextState;
    return nextState;
  };
}
